import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { getModuleIcon, getModuleIconColor, MODULE_ICON_OPTIONS } from '@/lib/moduleIcons';

const DEFAULT_ICON_KEY = 'book-open';

interface ModuleIconPickerProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
}

export function ModuleIconPicker({ value, onChange, id }: ModuleIconPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentKey = value?.trim() || DEFAULT_ICON_KEY;
  const CurrentIcon = getModuleIcon(currentKey);
  const currentColorClass = getModuleIconColor(currentKey);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 w-full min-h-[2.5rem] rounded-md border border-input bg-background px-3 py-2 text-left hover:bg-muted/50 transition-colors"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Choose icon"
      >
        <span className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${currentColorClass}`}>
          <CurrentIcon className="w-5 h-5" />
        </span>
        <span className="text-sm text-muted-foreground flex-1">Tap to change icon</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 rounded-xl border border-border bg-card shadow-lg p-3"
          role="listbox"
          aria-label="Icon options"
        >
          <p className="text-xs font-medium text-muted-foreground mb-1.5 px-0.5">Choose an icon</p>
          <div className="grid grid-cols-7 gap-1.5 max-h-[200px] overflow-y-auto">
            {MODULE_ICON_OPTIONS.map((opt) => {
              const isSelected = opt.value === currentKey;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`flex flex-col items-center justify-center gap-0 p-1.5 rounded-md border transition-all min-w-0 ${
                    isSelected
                      ? 'border-accent bg-accent/10'
                      : 'border-transparent hover:bg-muted hover:border-border'
                  }`}
                  title={opt.label}
                >
                  <span className={`shrink-0 ${opt.colorClass}`}>
                    <opt.Icon className="w-4 h-4" />
                  </span>
                  <span className="text-[9px] text-muted-foreground truncate w-full text-center leading-tight">
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
